import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";
import { API_URL } from "@/config/env";

class AxiosInstanceManager {
  private static instance: AxiosInstanceManager;
  private axiosInstance: AxiosInstance;
  private isRefreshing = false;
  private refreshPromise: Promise<boolean> | null = null;
  private lastRefreshTime: number = 0;
  private readonly REFRESH_COOLDOWN_MS = 5000;

  private constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_URL,
      withCredentials: true,
    });
    this.setupInterceptors();
  }

  static getInstance(): AxiosInstanceManager {
    if (!AxiosInstanceManager.instance) {
      AxiosInstanceManager.instance = new AxiosInstanceManager();
    }
    return AxiosInstanceManager.instance;
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Inject access token from localStorage
        const token = localStorage.getItem("access_token");
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // Handle FormData content-type
        if (config.data instanceof FormData && config.headers) {
          delete (config.headers as Record<string, unknown>)["Content-Type"];
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & {
          _retry?: boolean;
        };
        if (!originalRequest) return Promise.reject(error);

        const url = originalRequest.url || "";
        const isAuthEndpoint =
          url.includes("/accounts/login") ||
          url.includes("/accounts/refresh-access-token") ||
          url.includes("/accounts/logout");

        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          !isAuthEndpoint
        ) {
          // Cooldown check
          const elapsed = Date.now() - this.lastRefreshTime;
          if (elapsed < this.REFRESH_COOLDOWN_MS && this.refreshPromise) {
            return this.refreshPromise.then((ok) =>
              ok
                ? this.axiosInstance(originalRequest)
                : Promise.reject(error),
            );
          }

          if (this.isRefreshing && this.refreshPromise) {
            return this.refreshPromise.then((ok) =>
              ok
                ? this.axiosInstance(originalRequest)
                : Promise.reject(error),
            );
          }

          originalRequest._retry = true;
          this.isRefreshing = true;
          this.refreshPromise = this.performTokenRefresh();

          return this.refreshPromise
            .then((ok) => {
              if (ok) {
                this.lastRefreshTime = Date.now();
                return this.axiosInstance(originalRequest);
              }
              throw new Error("Token refresh failed");
            })
            .catch((refreshErr) => {
              this.clearSession();
              return Promise.reject(refreshErr);
            });
        }

        return Promise.reject(error);
      },
    );
  }

  private async performTokenRefresh(): Promise<boolean> {
    try {
      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) return false;

      const resp = await axios.post(
        `${this.axiosInstance.defaults.baseURL}/accounts/refresh-access-token/`,
        import.meta.env.DEV ? { refresh_token: refreshToken } : {},
        { withCredentials: true },
      );

      if (resp.status === 200 && resp.data.access_token) {
        localStorage.setItem("access_token", resp.data.access_token);
        this.lastRefreshTime = Date.now();
        return true;
      }
      return false;
    } catch {
      return false;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private clearSession(): void {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("user");
    localStorage.removeItem("user_roles");
    window.dispatchEvent(new CustomEvent("auth:session-expired"));
  }

  // Public HTTP helpers
  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.put<T>(url, data, config);
    return response.data;
  }

  async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.axiosInstance.delete<T>(url, config);
    return response.data;
  }

  async refreshToken(): Promise<boolean> {
    return this.performTokenRefresh();
  }

  getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

export const axiosInstance = AxiosInstanceManager.getInstance();
export default axiosInstance;
